import { useEffect, useRef } from "react";
import { addDays, formatShortDate, formatWeekday } from "../format";
import { coversDay, shortNames, timeLabel, type GridEvent, type GridPerson } from "./PersonWeekGrid";
import { timetableClasses } from "../timetablePresentation";
import { timetableChange } from "../timetablePresentation";
import { useSession } from "../session";
import { useFamilyNow } from "./useFamilyNow";
import { agendaPeriod } from "../agendaGroups";
import { importedSourceLabel, isImportedSource } from "../importedSources";

/**
 * Woche als Liste Tag für Tag (Handy): volle Breite für Titel, Personen als Kürzel mit Farbe und Namen im Tooltip.
 * Springt beim Öffnen und bei „Heute“ zum Fokustag (heute bzw. dem aus der Monatsansicht gewählten Tag).
 */
export function WeekAgenda({
  persons,
  events,
  from,
  today,
  focusDay,
  focusToken = 0,
  ready = true,
  days: dayCount = 7,
}: {
  persons: GridPerson[];
  events: GridEvent[];
  from: string;
  today: string;
  /** Tag, zu dem gescrollt wird (null = Wochenanfang). */
  focusDay: string | null;
  /** Ändert sich bei erneutem „Heute“-Tippen, damit auch ohne Wochenwechsel wieder hingesprungen wird. */
  focusToken?: number;
  /** Erst springen, wenn die Termine geladen sind (sonst ist die Seite noch zu kurz). */
  ready?: boolean;
  /** Ein Tag für die Tagesansicht, sieben für die Wochenansicht. */
  days?: number;
}) {
  const { me } = useSession();
  const now = useFamilyNow(me.family.timeZone);
  const short = shortNames(persons);
  const known = new Set(persons.map((p) => p.id));
  const byId = new Map(persons.map((p) => [p.id, p]));
  const days = Array.from({ length: dayCount }, (_, i) => addDays(from, i));
  const sorted = [...events].sort((a, b) => Number(b.allDay) - Number(a.allDay) || a.start.localeCompare(b.start));
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = focusDay ? ref.current?.querySelector<HTMLElement>(`[data-day="${focusDay}"]`) : null;
    if (target) target.scrollIntoView?.({ block: "start" });
    else window.scrollTo({ top: 0 });
  }, [from, focusDay, focusToken, ready]);

  return (
    <div className="agenda" ref={ref}>
      {days.map((day) => {
        const items = sorted.filter((e) => coversDay(e, day));
        const finished = items.filter(e => day === today && e.source === "web_untis" && !timetableChange(e.source, e.title) && agendaPeriod(e, now) === "past");
        const renderItems = (entries: GridEvent[]) => entries.map((e) => {
          const color = byId.get(e.personIds[0])?.color ?? "var(--primary)";
          return <li key={e.key}><button className={`agenda-event${timetableClasses(e.source, e.title)}`} data-source={e.source} onClick={e.onSelect} disabled={!e.onSelect} style={{ borderLeftColor: color, background: "var(--event-bg, var(--surface))" }}><span className="t">{timeLabel(e)}</span><span className="title">{e.title}</span><span className="meta">{e.personIds.map(id => byId.get(id)?.name).filter(Boolean).join(", ")}</span></button></li>;
        });
        const next = items.find(e => !e.allDay && e.start >= now && timetableChange(e.source, e.title) !== "cancelled");
        return (
          <section key={day} data-day={day} className={`agenda-day${day === today ? " today" : day < today ? " past" : ""}`} aria-label={`${formatWeekday(day)} ${formatShortDate(day)}`}>
            <h3 className="agenda-head">
              <span className="dow">{formatWeekday(day)}</span>
              <span className="date">{formatShortDate(day).split(" ")[1]}</span>
              {day === today && <span className="today-tag">Heute</span>}
            </h3>
            {items.length === 0 ? (
              <p className="agenda-empty">{dayCount === 1 ? "Keine Termine an diesem Tag" : "–"}</p>
            ) : (
              <ul className="agenda-list">
                {items.filter(e => !finished.includes(e)).map((e) => {
                  const mine = e.personIds.filter((id) => known.has(id));
                  const forAll = persons.length > 1 && persons.every((p) => mine.includes(p.id));
                  const color = forAll ? "var(--primary)" : mine.length > 0 ? byId.get(mine[0])!.color : "#8a96a3";
                  const creator = !isImportedSource(e.source) && e.createdByPersonId && known.has(e.createdByPersonId) && !mine.includes(e.createdByPersonId) ? byId.get(e.createdByPersonId) : undefined;
                  return (
                    <li key={`${e.key}-${day}`}>
                      <button type="button" className={`agenda-event${timetableClasses(e.source, e.title)}${day === today && e === next ? " up-next" : ""}`} data-source={e.source}
                        style={{ borderLeftColor: `var(--event-accent, ${color})`, background: "var(--event-bg, var(--surface))" }}
                        title={importedSourceLabel(e.source) ?? undefined} onClick={e.onSelect} disabled={!e.onSelect}>
                        <span className="t">{timeLabel(e)}</span>
                        <span className="title">{e.title}</span>
                        <span className="meta">
                          {forAll ? (
                            <span className="mini all">Alle</span>
                          ) : mine.length === 0 ? (
                            <span className="mini none" title="Ohne Person">?</span>
                          ) : (
                            mine.map((id) => (
                              <span key={id} className="mini" style={{ background: byId.get(id)!.color }} title={byId.get(id)!.name}>
                                {short.get(id)}
                              </span>
                            ))
                          )}
                          {!forAll && mine.length > 0 && <span className="names">{mine.map((id) => byId.get(id)!.name).join(", ")}</span>}
                          {e.location && <span className="loc">📍 {e.location}</span>}
                          {creator && <span className="by">von {creator.name}</span>}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {finished.length > 0 && <details className="past-events"><summary>Unterricht vorbei ({finished.length})</summary><ul className="agenda-list">{renderItems(finished)}</ul></details>}
          </section>
        );
      })}
    </div>
  );
}
